<script lang="ts">
	import type { PageProps } from "./$types";
	import { PUBLIC_GOOGLE_PLACES_TOKEN } from "$app/env/public";
	import { WhyChooseUs, OfferForm } from "#lib/web-ui/home-buyers-template/content/index.js";
	import Section from "#lib/web-ui/primitives/section.svelte";

	import MapPinIcon from "@lucide/svelte/icons/map-pin";
	import PhoneIcon from "@lucide/svelte/icons/phone";
	import MailIcon from "@lucide/svelte/icons/mail";
	import { getTemplateContext } from "#lib/web-ui/home-buyers-template/template-context.svelte.js";

	const templateContext = getTemplateContext();

	const pageUrl = `${templateContext.siteUrl}contact/`;
	const pageTitle = `Contact ${templateContext.siteName} - Get Your Free Cash Offer Today`;
	const pageDescription = `Contact ${templateContext.siteName} for a free, no-obligation cash offer on your home. Call ${templateContext.contact.phone} or fill out our form to get started today.`;
</script>

<svelte:head>
	<title>{pageTitle}</title>
	<meta name="description" content={pageDescription} />

	<!-- Open Graph -->
	<meta property="og:type" content="website" />
	<meta property="og:title" content={pageTitle} />
	<meta property="og:description" content={pageDescription} />
	<meta property="og:url" content={pageUrl} />
</svelte:head>

<Section>
	<div class="grid grid-cols-1 gap-8 lg:grid-cols-2">
		<!-- Content Column -->
		<div class="flex flex-col justify-center py-16 lg:py-0">
			<div class="max-w-2xl lg:max-w-none">
				<h1
					class="text-foreground mt-2 text-4xl font-semibold tracking-tight text-pretty sm:text-5xl"
				>
					Contact Us Today
				</h1>
				<p class="text-muted-foreground mt-6 text-xl/8">
					{templateContext.siteName} has purchased thousands of houses and we can buy yours
					in just a few days. There is no obligation, so contact us today and we will share
					some house selling tips too!
				</p>
				<div class="text-foreground/70 mt-10 max-w-xl lg:max-w-none">
					<ul role="list" class="text-foreground/70 mt-8 space-y-4 font-medium">
						<li class="flex items-start gap-x-3">
							<div class="flex-none">
								<div
									class="bg-primary/20 flex size-10 items-center justify-center rounded-full"
								>
									<MapPinIcon class="text-primary size-5" />
								</div>
							</div>
							<span class="flex items-center pt-1">
								{templateContext.contact.streetAddress}<br />
								{templateContext.contact.city}, {templateContext.contact.state}
								{templateContext.contact.zipCode}
							</span>
						</li>
						<li class="flex items-start gap-x-3">
							<div class="flex-none">
								<div
									class="bg-primary/20 flex size-10 items-center justify-center rounded-full"
								>
									<PhoneIcon class="text-primary size-5" />
								</div>
							</div>
							<a
								href="tel:{templateContext.contact.phone}"
								class="flex items-center pt-1 hover:underline"
							>
								{templateContext.contact.phone}
							</a>
						</li>
						<li class="flex items-start gap-x-3">
							<div class="flex-none">
								<div
									class="bg-primary/20 flex size-10 items-center justify-center rounded-full"
								>
									<MailIcon class="text-primary size-5" />
								</div>
							</div>
							<a
								href="mailto:{templateContext.contact.email}"
								class="flex items-center pt-1 hover:underline"
							>
								{templateContext.contact.email}
							</a>
						</li>
					</ul>
					<div class="bg-border/50 my-auto mt-6 rounded-lg text-center">
						<div class="aspect-[4/3] w-full">
							<iframe
								title="Property Location"
								width="100%"
								height="100%"
								frameborder="0"
								loading="lazy"
								style="border:0"
								src={`https://www.google.com/maps/embed/v1/place?key=${PUBLIC_GOOGLE_PLACES_TOKEN}&q=${encodeURIComponent(`${templateContext.contact.streetAddress}, ${templateContext.contact.city}, ${templateContext.contact.state} ${templateContext.contact.zipCode}`)}`}
								allowfullscreen
							></iframe>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Form Column -->
		<div class="bg-border/50 space-y-6 rounded-lg p-8">
			<div>
				<h2
					class="text-primary text-4xl font-semibold tracking-tight text-pretty sm:text-5xl dark:text-white"
				>
					Get Your No Obligation<br />Cash Offer
				</h2>

				<p class="mt-6 text-lg/8 text-gray-600 dark:text-gray-400">
					Call us today at
					<span class="font-bold">{templateContext.contact.phone}</span>
					. Get a cash offer to sell your house fast in as little as 24 hours. It’s Quick and
					Easy!
				</p>
			</div>

			<OfferForm />
		</div>
	</div>
</Section>

<WhyChooseUs />
